import {FC, useEffect, useState} from 'react'

import {SplitButton} from 'primereact/splitbutton'

import {Chip} from 'primereact/chip'
import {useNavigate} from 'react-router-dom'
import {
  fetchCustomers,
  getCustomers,
  getSelectedCustomer,
  removeCustomer,
  setDetailShow,
  setEditCustomer,
  setSelectedCustomer,
} from '../../../store/slices/customer.slice'
import {DatatableComponent} from '../../shared/DatatableComponent/DataTableComponent'
import {CustomerEditor} from '../CustomerEditor/CustomerEditor'
import {useAppDispatch, useAppSelector} from '../../../hooks'
import {OlangItem} from '../../shared/Olang/user-interface/OlangItem/OlangItem'
import {Image} from 'primereact/image'
import {ConfirmBoxComponent} from '../../shared/ConfirmBoxComponent/ConfirmBoxComponent'
import ButtonComponent from '../../shared/ButtonComponent/ButtonComponent'
import {setAlertParams} from '../../../store/slices/alert.slice'
import {API_BASE_URL_IMAGE} from '../../../api/config'
import {fetchValidator} from '../../Inventory/slice/inventory.slice'
import { PageHeader } from '../../shared/PageHeader/PageHeader'
import {DataTableComponent} from '@omniyat/reactcomponents'

export const CustomerList = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const list = useAppSelector(getCustomers)
  const selectedCustomer = useAppSelector(getSelectedCustomer)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingButton, setIsLoadingButton] = useState(false)

  let actions = [
    {
      label: 'Detail',
      icon: 'pi pi-eye text-blue-500',
      command: (e) => {
        setIsLoading(true)
        dispatch(fetchValidator('customer'))
        dispatch(setSelectedCustomer(e.item.data))
        dispatch(setDetailShow(false))
      },
    },
    {
      label: 'Supprimer',
      icon: 'pi pi-trash text-red-500',
      command: (e) => {
        dispatch(setSelectedCustomer(e.item.data))
        dispatch(
          setAlertParams({
            title: 'Supprimer',
            message: 'Voulez-vous vraiment supprimerce client?',
            acceptClassName: 'p-button-danger',
            visible: true,
            accept: () => {
              dispatch(removeCustomer(e.item.data))
            },
          })
        )
      },
    }
  ]

  const imageTemplate = (rowData) => {
    const imageSrc = rowData?.image ? `${API_BASE_URL_IMAGE}${rowData.image}` : null
    
    if (!imageSrc) {
      return (
        <div 
          className='flex align-items-center justify-content-center rounded-full bg-gradient-to-br from-blue-100 to-blue-200 shadow-2'
          style={{width: '70px', height: '70px', minWidth: '70px'}}
        >
          <i className="fas fa-building text-blue-600" style={{fontSize: '28px'}}></i>
        </div>
      )
    }
    
    return (
      <img
        src={imageSrc}
        alt='CustomerImage'
        width='70'
        height='70'
        className='image-preview rounded-full shadow-3 border-2 border-white'
        style={{objectFit: 'cover', minWidth: '70px'}}
        preview
        onError={(e) => {
          e.target.style.display = 'none'
          e.target.parentElement.innerHTML = `
            <div class="flex align-items-center justify-content-center rounded-full bg-gradient-to-br from-blue-100 to-blue-200 shadow-2" style="width: 70px; height: 70px; min-width: 70px;">
              <i class="fas fa-building text-blue-600" style="font-size: 28px;"></i>
            </div>
          `
        }}
      />
    )
  }

  // Template amélioré pour le nom du client
  const clientNameTemplate = (rowData) => {
    return (
      <div className="flex flex-column gap-1">
        <div className="font-bold text-xl text-900 line-height-3">
          {rowData.label || 'Client sans nom'}
        </div>
        <div className="text-sm text-600 font-medium">
          <i className="pi pi-building mr-2 text-blue-500"></i>
          Entreprise
        </div>
      </div>
    )
  }

  // Template amélioré pour le code client
  const clientCodeTemplate = (rowData) => {
    return (
      <div className="flex flex-column gap-2">
        <div className="flex align-items-center gap-2">
          <i className="pi pi-hashtag text-blue-500 font-bold"></i>
          <span className="font-bold text-lg text-primary">
            {rowData.code || 'N/A'}
          </span>
        </div>
        <div className="inline-flex align-items-center px-2 py-1 bg-blue-50 text-blue-700 border-round text-xs font-semibold">
          <i className="pi pi-tag mr-1"></i>
          Code Client
        </div>
      </div>
    )
  }

  // Template amélioré pour le nombre d'engins
  const enginCountTemplate = (rowData) => {
    const count = rowData.enginNumber || 0
    const severity = count > 10 ? 'success' : count > 5 ? 'warning' : count > 0 ? 'info' : 'secondary'
    const bgColor = count > 10 ? 'bg-green-50' : count > 5 ? 'bg-orange-50' : count > 0 ? 'bg-blue-50' : 'bg-gray-50'
    const textColor = count > 10 ? 'text-green-700' : count > 5 ? 'text-orange-700' : count > 0 ? 'text-blue-700' : 'text-gray-600'
    const icon = count > 10 ? 'pi-check-circle' : count > 5 ? 'pi-exclamation-triangle' : count > 0 ? 'pi-info-circle' : 'pi-minus-circle'
    
    return (
      <div className="flex flex-column gap-2 align-items-center">
        <div className={`flex align-items-center justify-content-center w-4rem h-4rem border-circle ${bgColor} shadow-2`}>
          <span className={`font-bold text-2xl ${textColor}`}>
            {count}
          </span>
        </div>
        <div className={`inline-flex align-items-center px-3 py-1 ${bgColor} ${textColor} border-round text-sm font-semibold`}>
          <i className={`pi ${icon} mr-2`}></i>
          {count === 0 ? 'Aucun engin' : count === 1 ? '1 engin' : `${count} engins`}
        </div>
      </div>
    )
  }

  const columns = [
    {
      field: null, 
      header: 'Logo', 
      olang: 'Image', 
      body: imageTemplate,
      style: { width: '100px', textAlign: 'center' }
    },
    {
      field: 'label', 
      header: 'Nom du Client', 
      olang: 'Nom.client', 
      filter: true,
      body: clientNameTemplate,
      style: { minWidth: '250px' }
    },
    {
      field: 'code', 
      header: 'Code Client', 
      olang: 'Code.client', 
      filter: true,
      body: clientCodeTemplate,
      style: { width: '200px' }
    },
    {
      field: 'enginNumber', 
      header: "Équipements", 
      olang: 'Nombre.engin', 
      filter: true,
      body: enginCountTemplate,
      style: { width: '180px', textAlign: 'center' }
    },
  ]

  const exportFields = [
    {label: 'Code client', column: 'code'},
    {label: 'NOM client', column: 'label'},
    {label: "Nombre d'engin", column: 'enginNumber'},
  ]
  const rowGroupTemplates = {
    Nom: (rowData) => <Chip label={rowData?.label} />,
    Code: (rowData) => <Chip label={rowData?.code} />,
    enginNumber: (rowData) => <Chip label={rowData?.enginNumber} />,
  }

  useEffect(() => {
    dispatch(fetchCustomers())
  }, [])

  const create = () => {
    setIsLoadingButton(true)
    dispatch(fetchValidator('customer'))
      .then((res) => {
        if (res.payload) {
          dispatch(setSelectedCustomer(null))
          dispatch(setEditCustomer(true))
        }
      })
      .finally(() => {
        setIsLoadingButton(false)
      })
  }

  return (
    <>
      {/* Page Header */}
      <PageHeader
        titleOlang="customer.list"
        backPath="/dashboard"
        info={
          <div className="flex align-items-center gap-3">
            <div className="flex align-items-center gap-2">
              <i className="pi pi-users text-xl text-primary"></i>
              <span className="text-lg font-semibold">
                {list?.length || 0} client{(list?.length || 0) > 1 ? 's' : ''}
              </span>
            </div>
            <div className="border-left-2 border-300" style={{ height: '30px' }}></div>
          </div>
        }
        className="m-3"
      />
      
      <DataTableComponent
        tableId='customer-table'
        data={list}
        columns={columns}
        onNew={create}
        isLoading={isLoadingButton}
        exportFields={exportFields}
        rowGroupTemplates={rowGroupTemplates}
        tableOptions={{  className: 'datatable-border-2' }}
        onSelections={(r) => console.log('data:', r)}
        rowActions={actions}
        className="shadow-3"
        style={{ borderRadius: '12px', overflow: 'hidden' }}
      />
      <CustomerEditor />
    </>
  )
}
